"use client";

import React from "react";
import { createPortal } from "react-dom";
import { Heart, Flag, Check } from "lucide-react";
import ReportModal from "@/components/feed/ReportModal";
import { useToast } from "@/components/ui/toast";
import { buildShareUrls } from "@/lib/share";
import { track } from "@/lib/analytics";

type DetailType = "video" | "gallery" | "post" | "profile";

export default function LikeShareReport({
  type,
  id,
  liked,
  likeCount,
  likeable = true,
  onToggleLike,
  share = true,
  report = true,
  reportUserId,
  showCounts = false,
  compact = false,
}: {
  type: DetailType;
  id?: number | string | null;
  liked?: boolean;
  likeCount?: number;
  likeable?: boolean;
  onToggleLike?: () => Promise<{ liked: boolean; likeCount?: number } | void>;
  share?: boolean;
  report?: boolean;
  reportUserId?: string | null;
  /** When true, shows numeric counts next to labels */
  showCounts?: boolean;
  /** Smaller paddings/icons for constrained layouts */
  compact?: boolean;
}) {
  const [copied, setCopied] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [shareOpen, setShareOpen] = React.useState(false);
  const [shareCoords, setShareCoords] = React.useState<{ top: number; left: number } | null>(null);
  const shareBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const shareMenuRef = React.useRef<HTMLDivElement | null>(null);
  const { toast } = useToast();

  // Anchor the share menu to the button via fixed coords so it can portal to
  // <body> and escape any `overflow-hidden` ancestor (banner/hero card).
  const positionShareMenu = React.useCallback(() => {
    const btn = shareBtnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const menuWidth = 224; // matches min-w-[14rem]
    const left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, r.right - menuWidth));
    const top = r.bottom + 8;
    setShareCoords({ top, left });
  }, []);

  React.useEffect(() => {
    if (!shareOpen) return;
    positionShareMenu();
    const onScroll = () => positionShareMenu();
    const onResize = () => positionShareMenu();
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (shareMenuRef.current?.contains(t)) return;
      if (shareBtnRef.current?.contains(t)) return;
      setShareOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShareOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [shareOpen, positionShareMenu]);

  const btnBase =
    `inline-flex items-center justify-center rounded-full ${compact ? 'h-8 w-8' : 'h-[34px] w-[34px]'} ` +
    'transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 active:scale-95';

  const onShareCopy = async () => {
    try {
      const href = typeof window !== 'undefined' ? window.location.href : '';
      if (!href) return;
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        {likeable && (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={async () => { if (onToggleLike) { await onToggleLike(); try { track('like', { type, id }); } catch {} } }}
              className={
                btnBase + ' ' +
                (liked
                  ? 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400 hover:bg-pink-200 dark:hover:bg-pink-900/40'
                  : 'bg-muted hover:bg-pink-50 dark:hover:bg-pink-900/20 text-muted-foreground hover:text-pink-600 dark:hover:text-pink-400')
              }
              aria-pressed={!!liked}
              title={liked ? 'Unlike' : 'Like'}
              aria-label={`${liked ? 'Unlike' : 'Like'}${typeof likeCount === 'number' && likeCount > 0 ? ` (${likeCount})` : ''}`}
            >
              <Heart className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} ${liked ? 'fill-current' : ''}`} />
            </button>
            {typeof likeCount === 'number' && likeCount > 0 && (
              <span className="text-sm text-muted-foreground font-medium min-w-[20px]">
                {likeCount.toLocaleString()}
              </span>
            )}
          </div>
        )}

        {share && (
          <button
            ref={shareBtnRef}
            type="button"
            onClick={() => setShareOpen((o) => !o)}
            className={
              btnBase + ' bg-muted hover:bg-blue-50 dark:hover:bg-blue-900/20 text-muted-foreground hover:text-blue-600 dark:hover:text-blue-400'
            }
            aria-expanded={shareOpen}
            aria-haspopup="menu"
            title="Share"
            aria-label="Share"
          >
            <svg className={`${compact ? 'h-4 w-4' : 'h-5 w-5'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
          </button>
        )}

        {(() => {
          const canReport = report && ((type === 'profile' && !!reportUserId) || (type !== 'profile' && id != null));
          return canReport;
        })() && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className={btnBase + ' bg-muted hover:bg-red-50 dark:hover:bg-red-900/20 text-muted-foreground hover:text-red-600 dark:hover:text-red-400'}
            title="Report"
            aria-label="Report"
          >
            <Flag className={`${compact ? 'h-4 w-4' : 'h-5 w-5'}`} />
          </button>
        )}
      </div>
      {report && (
        <ReportModal
          open={open}
          onClose={() => setOpen(false)}
          target={type === 'profile'
            ? (reportUserId ? { id: reportUserId, type: 'profile' } : null)
            : (id != null ? { id: id as any, type: (type as any) } : null)
          }
        />
      )}
      {shareOpen && shareCoords && typeof document !== 'undefined' && createPortal(
        <div
          ref={shareMenuRef}
          role="menu"
          className="fixed z-[100] min-w-[14rem] rounded-md border shadow-lg p-2 bg-white text-foreground dark:bg-zinc-900 dark:text-zinc-100"
          style={{ top: shareCoords.top, left: shareCoords.left }}
        >
          {(() => {
            const href = typeof window !== 'undefined' ? window.location.href : '';
            const links = buildShareUrls(href, undefined);
            return (
              <ul>
                <li>
                  <button
                    className="w-full text-left px-3 py-2 rounded hover:bg-accent"
                    onClick={async () => {
                      try { await navigator.clipboard.writeText(links.copy); setCopied(true); toast({ title: 'Link copied', variant: 'info', duration: 1800 }); } catch {}
                      try { track('share', { platform: 'copy', type, id }); } catch {}
                      setShareOpen(false);
                    }}
                  >Copy link</button>
                </li>
                <li><a href={links.email} className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'email', type, id }); } catch {}; setShareOpen(false); }}>Email</a></li>
                <li><a href={links.x} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'x', type, id }); } catch {}; setShareOpen(false); }}>X</a></li>
                <li><a href={links.bluesky} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'bluesky', type, id }); } catch {}; setShareOpen(false); }}>Bluesky</a></li>
                <li><a href={links.telegram} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'telegram', type, id }); } catch {}; setShareOpen(false); }}>Telegram</a></li>
                <li><a href={links.reddit} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'reddit', type, id }); } catch {}; setShareOpen(false); }}>Reddit</a></li>
                <li><a href={links.facebook} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'facebook', type, id }); } catch {}; setShareOpen(false); }}>Facebook</a></li>
                <li><a href={links.whatsapp} target="_blank" rel="noopener noreferrer" className="block px-3 py-2 rounded hover:bg-accent" onClick={() => { try { track('share', { platform: 'whatsapp', type, id }); } catch {}; setShareOpen(false); }}>WhatsApp</a></li>
              </ul>
            );
          })()}
        </div>,
        document.body
      )}
    </>
  );
}
